var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { Badge } from '../Badge/index.js';
import { Icon } from '../Icon/index.js';
import { SelectorIcon, XIcon } from '../icons/index.js';
import { useFloatingContext } from '../utils/floating/index.js';
import { useInputColors } from '../utils/styles/index.js';
import { useMultiselect } from './MultiselectContext.js';
export const MultiselectButton = forwardRef((props, ref) => {
    const { children, className, placeholder, dense = false, leadingIcon } = props, rest = __rest(props, ["children", "className", "placeholder", "dense", "leadingIcon"]);
    const { inputValue, searchable, disabled, invalid, isOpen, getComboboxProps, getInputProps, getDropdownProps, getToggleButtonProps, setSelectedItems, selectedItems, addSelectedItem, reset, onCreateItem, } = useMultiselect();
    const { reference } = useFloatingContext();
    const { backgroundClsx, borderClsx, invalidClsx, disabledClsx, iconClsx, placeholderClsx, fontClsx, focusClsx, } = useInputColors({
        invalid,
        disabled,
    });
    const onKeyDown = event => {
        if (event.key === 'Backspace' && !inputValue) {
            setSelectedItems(selectedItems.slice(0, selectedItems.length - 1));
            reset();
        }
        if (event.key === 'Escape') {
            event.stopPropagation();
        }
    };
    const onPaste = event => {
        var _a;
        if (!onCreateItem || !!inputValue) {
            return;
        }
        const content = (_a = event === null || event === void 0 ? void 0 : event.clipboardData) === null || _a === void 0 ? void 0 : _a.getData('Text');
        if (!content) {
            return;
        }
        const items = content
            .split(/[\n,]+/)
            .map(it => it.trim())
            .filter(it => !!it);
        const uniqueItems = [...new Set(items)];
        uniqueItems === null || uniqueItems === void 0 ? void 0 : uniqueItems.forEach(item => {
            if (item && !selectedItems.includes(item)) {
                addSelectedItem(item);
                onCreateItem(item);
            }
        });
        event.preventDefault();
    };
    return (React.createElement("div", { ref: reference },
        React.createElement("div", Object.assign({ className: clsx('block w-full rounded-md group relative', 'py-2 text-start cursor-default', [
                'appearance-none',
                'text-base',
                fontClsx,
                backgroundClsx,
                !dense && borderClsx,
                !dense && focusClsx,
                invalid && invalidClsx,
                disabled && disabledClsx,
            ], !dense && 'ps-3 pe-10', className) }, getComboboxProps(getToggleButtonProps(Object.assign({ disabled, ref }, rest)))),
            React.createElement("span", { className: "block truncate" },
                React.createElement("div", { className: "flex flex-wrap -ms-2 -mt-2" },
                    leadingIcon && (React.createElement(Icon, { className: clsx('ms-2 mt-2', 'h-5 w-5', !!children && 'me-1') }, leadingIcon)),
                    children,
                    React.createElement("input", Object.assign({ className: clsx('focus:outline-none bg-transparent flex-1 ms-2 mt-2', 'text-base', fontClsx, placeholderClsx, !searchable && ' caret-transparent') }, getInputProps(Object.assign(Object.assign({}, getDropdownProps({
                        preventKeyAction: isOpen,
                    })), { placeholder,
                        disabled,
                        onKeyDown,
                        onPaste })), { readOnly: !searchable })))),
            !dense && (React.createElement("span", { className: "absolute inset-y-0 end-0 flex items-center pe-2 pointer-events-none", "aria-label": "toggle menu" },
                React.createElement(SelectorIcon, { className: clsx('w-5 h-5', iconClsx), "aria-hidden": "true" }))))));
});
export const MultiselectSelectedItem = props => {
    const { value, index, children, className } = props, rest = __rest(props, ["value", "index", "children", "className"]);
    const { removeSelectedItem, getSelectedItemProps, disabled } = useMultiselect();
    return (React.createElement(Badge, Object.assign({ variant: "secondary", trailingIcon: React.createElement(Badge.Button, { onClick: e => {
                e.stopPropagation();
                removeSelectedItem(value);
            }, disabled: disabled },
            React.createElement("span", { className: "sr-only" }, "Remove option"),
            React.createElement(Icon, { className: "h-2 w-2" },
                React.createElement(XIcon, null))), className: clsx('ms-2 mt-2 truncate', className) }, rest, getSelectedItemProps({ selectedItem: value, index })),
        React.createElement("div", { className: "truncate" }, children)));
};
//# sourceMappingURL=MultiselectButton.js.map